import { type AtmosphereDef, VERDANT_AIR } from '../../engine/atmosphere/AtmosphereDef';
import type { AmbienceDef } from '../../engine/audio/Ambience';
import type { GlowDef, LightRig, VeilDef } from '../../engine/lighting/LightRig';
import { type BiomePalette, biomeStyle } from '../../engine/terrain/biomePalette';
import type { MouthRockStyle } from '../../engine/terrain/MouthRocks';
import { type TerrainProfile, VERDANT_TERRAIN } from '../../engine/terrain/TerrainProfile';
import type { TerrainStyle } from '../../engine/terrain/TerrainStyle';
import type { GrassStyle, SpeciesStyle, TreeStyle } from '../../engine/vegetation/VegetationStyle';
import { EMBERDEEP_MOUTH, GLOWMIRE_MOUTH, UMBRAL_MOUTH, VERDANT_MOUTH } from '../hollows/caves';
import { EMBERDEEP, EMBERDEEP_AIR, EMBERDEEP_TERRAIN } from '../hollows/emberdeep';
import { GLOWMIRE, GLOWMIRE_AIR, GLOWMIRE_TERRAIN } from '../hollows/glowmire';
import { boulders } from '../hollows/rocks';
import { UMBRAL, UMBRAL_AIR, UMBRAL_TERRAIN } from '../hollows/umbral';
import { VERDANT } from '../hollows/verdant';
import type { HollowSceneDef } from '../scenes/HollowScene';
import type { BiomeId, BiomeSlot, MotesId, PlanetTypeId, PresetId, SkyId, SpeciesId } from './spec';

type RGB = [number, number, number];
type N = any;

/*
 * What custom worlds are made of: planet types (the lie of the land), skies (light and air),
 * biomes (ground, plants, cave mouths), tree species and presets that combine them. The four
 * built-in worlds give a type, a sky and a biome each; the rest are new.
 */

// --- Planet types -----------------------------------------------------------------------------

export interface PlanetType {
  label: string;
  /** Its terrain recipe, at `radius` (a custom world scales it to its own size). */
  terrain: TerrainProfile;
  radius: number;
  /** Its seas: water, or lava. */
  sea: 'water' | 'lava';
}

const V = VERDANT_TERRAIN;
export const PLANET_TYPES: Record<PlanetTypeId, PlanetType> = {
  continents: { label: 'Continents and seas', terrain: V, radius: 12_000, sea: 'water' },
  archipelago: {
    label: 'Archipelago: many islands',
    terrain: {
      ...V,
      continents: { wavelength: 4200, octaves: 5, lacunarity: 2.03, gain: 0.55 },
      continentHeight: 180,
      landBias: -0.1,
      mountainMask: { wavelength: 5000, octaves: 2, lacunarity: 2.1, gain: 0.5 },
      ridgeHeight: 650,
      hills: { wavelength: 600, octaves: 4, lacunarity: 2.05, gain: 0.5 },
      hillHeight: 45,
    },
    radius: 12_000,
    sea: 'water',
  },
  ocean: {
    label: 'Ocean: a few islands',
    terrain: {
      ...V,
      landBias: -0.3,
      continentHeight: 220,
      ridges: { wavelength: 2600, octaves: 8, lacunarity: 2.01, gain: 0.52 },
      ridgeHeight: 900,
    },
    radius: 12_000,
    sea: 'water',
  },
  highlands: {
    label: 'Highlands: mountains everywhere',
    terrain: {
      ...V,
      landBias: 0.5,
      continentHeight: 300,
      mountainMask: { wavelength: 5000, octaves: 2, lacunarity: 2.1, gain: 0.5 },
      ridgeHeight: 2000,
      ridgeDamping: 1.1,
    },
    radius: 12_000,
    sea: 'water',
  },
  plains: {
    label: 'Plains: rolling and gentle',
    terrain: {
      ...V,
      landBias: 0.35,
      continentHeight: 120,
      ridgeHeight: 260,
      ridgeDamping: 2.4,
      hills: { wavelength: 1200, octaves: 4, lacunarity: 2.05, gain: 0.5 },
      hillHeight: 70,
    },
    radius: 12_000,
    sea: 'water',
  },
  shattered: {
    label: 'Shattered: sharp ridges',
    terrain: {
      ...V,
      landBias: 0.3,
      mountainMask: { wavelength: 3000, octaves: 2, lacunarity: 2.1, gain: 0.5 },
      ridges: { wavelength: 1800, octaves: 8, lacunarity: 2.05, gain: 0.58 },
      ridgeHeight: 1500,
      ridgeDamping: 0.4,
    },
    radius: 12_000,
    sea: 'water',
  },
  marsh: { label: 'Marsh: low land and pools', terrain: GLOWMIRE_TERRAIN, radius: 5000, sea: 'water' },
  volcanic: { label: 'Volcanic: over a lava sea', terrain: EMBERDEEP_TERRAIN, radius: 8000, sea: 'lava' },
  cavern: { label: 'Cavern: rocky and closed in', terrain: UMBRAL_TERRAIN, radius: 3000, sea: 'water' },
};

// --- Skies --------------------------------------------------------------------------------------

export interface Sky {
  label: string;
  sun: { color: RGB; intensity: number } | null;
  veil: VeilDef;
  glow: GlowDef | null;
  /** Its air (for a world of `airRadius`; scaled to the world), cloud cover, exposure. */
  air: AtmosphereDef | null;
  airRadius: number;
  clouds: number;
  exposureKey: number;
  ambience: AmbienceDef;
  waterSkyTint: RGB;
  lantern: boolean;
  birds: boolean;
  motes: MotesId;
  /** Daylight spilling down a cave mouth. */
  leak: RGB;
}

// Scatter boosts and haze are lower than they were before the palettes moved toward real
// albedos: at the higher exposure the same air washed the far side out.
const GOLDEN_AIR: AtmosphereDef = {
  rayleigh: [7e-6, 11e-6, 17e-6],
  rayleighHeight: 3500,
  mie: 4.5e-6,
  mieHeight: 1000,
  mieAbsorption: 0.15,
  mieG: 0.75,
  ambientScatter: 0.6,
  scatterBoost: 1.4,
};
const PALE_AIR: AtmosphereDef = {
  rayleigh: [7.5e-6, 17.5e-6, 43e-6],
  rayleighHeight: 3000,
  mie: 3e-6,
  mieHeight: 700,
  mieAbsorption: 0.05,
  mieG: 0.8,
  ambientScatter: 0.7,
  scatterBoost: 1.7,
};
const ALIEN_AIR: AtmosphereDef = {
  rayleigh: [22e-6, 9e-6, 30e-6],
  rayleighHeight: 3500,
  mie: 7e-6,
  mieHeight: 900,
  mieAbsorption: 0.1,
  mieG: 0.7,
  ambientScatter: 0.6,
  scatterBoost: 1.7,
  airGlow: [0.05e-5, 0.15e-5, 0.1e-5],
};

export const SKIES: Record<SkyId, Sky> = {
  sunlit: {
    label: 'Sunlit: a sun, blue sky, clouds',
    sun: { color: VERDANT.sun!.color, intensity: VERDANT.sun!.intensity },
    veil: VERDANT.veil,
    glow: null,
    air: VERDANT_AIR,
    airRadius: 12_000,
    // As Verdant's: the far side overhead is the view, so it stays mostly open (0.42 before).
    clouds: 0.36,
    exposureKey: VERDANT.exposureKey,
    ambience: VERDANT.ambience!,
    waterSkyTint: VERDANT.waterSkyTint,
    lantern: false,
    birds: true,
    motes: 'fireflies',
    leak: VERDANT_MOUTH.leak,
  },
  golden: {
    label: 'Golden: a hot sun through dusty air',
    sun: { color: [1.0, 0.84, 0.6], intensity: 6.2 },
    veil: { ...VERDANT.veil, litFraction: 0.5, duskColor: [1.0, 0.42, 0.16] },
    glow: null,
    air: GOLDEN_AIR,
    airRadius: 12_000,
    clouds: 0.12,
    exposureKey: 0.38,
    ambience: {
      wind: { gain: 1.1, cutoff: 520 },
      events: [
        { kind: 'bird', rate: 0.08, gain: 0.035, when: 'day' },
        { kind: 'cricket', rate: 1.8, gain: 0.02, when: 'night' },
      ],
    },
    waterSkyTint: [1.0, 0.95, 0.8],
    lantern: false,
    birds: true,
    motes: 'none',
    leak: [0.75, 0.6, 0.4],
  },
  pale: {
    label: 'Frozen: a pale sun, crisp air',
    sun: { color: [0.86, 0.92, 1.0], intensity: 4.6 },
    veil: { ...VERDANT.veil, litFraction: 0.44, duskColor: [0.75, 0.6, 1.0], nightAmbient: [0.7, 0.85, 1.5] },
    glow: null,
    air: PALE_AIR,
    airRadius: 12_000,
    clouds: 0.35,
    exposureKey: 0.5,
    ambience: { wind: { gain: 1.0, cutoff: 900 }, events: [{ kind: 'drip', rate: 0.05, gain: 0.02 }] },
    waterSkyTint: [0.8, 0.95, 1.3],
    lantern: false,
    birds: false,
    motes: 'none',
    leak: [0.5, 0.55, 0.62],
  },
  alien: {
    label: 'Alien: a teal sun, violet sky',
    sun: { color: [0.55, 1.0, 0.85], intensity: 5 },
    veil: { ...VERDANT.veil, duskColor: [1.0, 0.25, 0.7], nightAmbient: [1.2, 0.8, 1.4] },
    glow: null,
    air: ALIEN_AIR,
    airRadius: 12_000,
    clouds: 0.3,
    exposureKey: 0.46,
    ambience: {
      wind: { gain: 0.5, cutoff: 500 },
      drone: { freqs: [98, 146.8], gain: 0.012 },
      events: [{ kind: 'chime', rate: 0.1, gain: 0.03 }],
    },
    waterSkyTint: [1.1, 0.8, 1.2],
    lantern: false,
    birds: false,
    motes: 'spores',
    leak: [0.5, 0.8, 0.7],
  },
  glowing: {
    label: 'Glowing: no sun, living light',
    sun: null,
    veil: GLOWMIRE.veil,
    glow: GLOWMIRE.glow!,
    air: GLOWMIRE_AIR,
    airRadius: GLOWMIRE.radius,
    clouds: 0,
    exposureKey: GLOWMIRE.exposureKey,
    ambience: GLOWMIRE.ambience!,
    waterSkyTint: GLOWMIRE.waterSkyTint,
    lantern: false,
    birds: false,
    motes: 'spores',
    leak: GLOWMIRE_MOUTH.leak,
  },
  ember: {
    label: 'Ember: lit from below, smoky',
    sun: null,
    veil: EMBERDEEP.veil,
    glow: EMBERDEEP.glow!,
    air: EMBERDEEP_AIR,
    airRadius: EMBERDEEP.radius,
    clouds: 0,
    exposureKey: EMBERDEEP.exposureKey,
    ambience: EMBERDEEP.ambience!,
    waterSkyTint: EMBERDEEP.waterSkyTint,
    lantern: false,
    birds: false,
    motes: 'embers',
    leak: EMBERDEEP_MOUTH.leak,
  },
  dark: {
    label: 'Dark: bring your lantern',
    sun: null,
    veil: UMBRAL.veil,
    glow: UMBRAL.glow!,
    air: UMBRAL_AIR,
    airRadius: UMBRAL.radius,
    clouds: 0,
    exposureKey: UMBRAL.exposureKey,
    ambience: UMBRAL.ambience!,
    waterSkyTint: UMBRAL.waterSkyTint,
    lantern: true,
    birds: false,
    motes: 'none',
    leak: UMBRAL_MOUTH.leak,
  },
};

// --- Tree species -------------------------------------------------------------------------------

export const SPECIES: Record<SpeciesId, { label: string; make: (rig: LightRig) => SpeciesStyle }> = {
  pine: { label: 'Pine', make: (rig) => VERDANT.trees(rig).a },
  oak: { label: 'Oak (turns in autumn)', make: (rig) => VERDANT.trees(rig).b },
  mushroom: { label: 'Giant glowing mushroom', make: (rig) => GLOWMIRE.trees(rig).a },
  lantern: { label: 'Lantern stalk', make: (rig) => GLOWMIRE.trees(rig).b },
  crystal: { label: 'Crystal', make: (rig) => UMBRAL.trees(rig).a },
  bone: { label: 'Bone-white tree', make: (rig) => UMBRAL.trees(rig).b },
  charred: { label: 'Charred pine', make: (rig) => EMBERDEEP.trees(rig).a },
  ember: { label: 'Smouldering ember tree', make: (rig) => EMBERDEEP.trees(rig).b },
  palm: {
    label: 'Palm',
    make: () => ({
      shape: 'palm',
      bark: [0.189, 0.151, 0.101],
      crown: [
        [0.054, 0.163, 0.034],
        [0.087, 0.198, 0.043],
      ],
      bendUp: 0.4,
    }),
  },
  cactus: {
    label: 'Cactus',
    make: () => ({
      shape: 'cactus',
      bark: [0.078, 0.17, 0.059],
      crown: [
        [0.067, 0.161, 0.054],
        [0.094, 0.188, 0.063],
      ],
      bendUp: 0.1,
      sway: 0,
    }),
  },
  acacia: {
    label: 'Acacia',
    make: () => ({
      shape: 'acacia',
      bark: [0.157, 0.114, 0.071],
      crown: [
        [0.085, 0.141, 0.035],
        [0.116, 0.167, 0.045],
      ],
      bendUp: 0.5,
    }),
  },
  deadtree: {
    label: 'Dead tree',
    make: () => ({
      shape: 'dead',
      bark: [0.16, 0.133, 0.113],
      crown: [
        [0.16, 0.133, 0.113],
        [0.185, 0.161, 0.136],
      ],
      bendUp: 0,
      sway: 0.3,
    }),
  },
  shrub: {
    label: 'Shrub',
    make: () => ({
      shape: 'shrub',
      bark: [0.146, 0.11, 0.073],
      crown: [
        [0.071, 0.142, 0.043],
        [0.103, 0.168, 0.052],
      ],
      bendUp: 0.5,
    }),
  },
  drybush: {
    label: 'Dry bush',
    make: () => ({
      shape: 'shrub',
      bark: [0.154, 0.119, 0.084],
      crown: [
        [0.166, 0.153, 0.077],
        [0.204, 0.18, 0.096],
      ],
      bendUp: 0.3,
      scale: 0.9,
    }),
  },
  snowpine: {
    label: 'Snowy pine',
    make: () => ({
      shape: 'conifer',
      bark: [0.128, 0.096, 0.072],
      crown: [
        [0.379, 0.414, 0.427],
        [0.565, 0.598, 0.615],
      ],
      bendUp: 0.6,
    }),
  },
  smallpine: {
    label: 'Stunted pine',
    make: () => ({
      shape: 'conifer',
      bark: [0.128, 0.096, 0.072],
      crown: [
        [0.039, 0.097, 0.058],
        [0.057, 0.114, 0.065],
      ],
      bendUp: 0.5,
      scale: 0.55,
    }),
  },
  jungletree: {
    label: 'Jungle giant',
    make: () => ({
      shape: 'broadleaf',
      bark: [0.146, 0.11, 0.073],
      crown: [
        [0.026, 0.12, 0.026],
        [0.049, 0.155, 0.028],
      ],
      bendUp: 0.5,
      scale: 1.45,
    }),
  },
  autumnoak: {
    label: 'Autumn oak',
    make: () => ({
      shape: 'broadleaf',
      bark: [0.146, 0.1, 0.061],
      crown: [
        [0.285, 0.127, 0.025],
        [0.348, 0.07, 0.021],
      ],
      bendUp: 0.55,
    }),
  },
  goldbirch: {
    label: 'Golden birch',
    make: () => ({
      shape: 'broadleaf',
      bark: [0.321, 0.304, 0.28],
      crown: [
        [0.308, 0.224, 0.028],
        [0.358, 0.173, 0.023],
      ],
      bendUp: 0.55,
      scale: 0.85,
    }),
  },
};

// --- Biomes -------------------------------------------------------------------------------------

export interface Biome {
  label: string;
  /** Its ground. */
  style: (rig: LightRig) => TerrainStyle;
  /** Its grass, and how much of it grows (0 … 1). */
  grass: (rig: LightRig) => GrassStyle;
  grassAmount: number;
  /** Its two tree species (a world may choose others), and how its forests and lone trees grow. */
  species: [SpeciesId, SpeciesId];
  trees: ((rig: LightRig) => Omit<TreeStyle, 'a' | 'b'>) | null;
  /** Its boulders. */
  rocks: ((rig: LightRig) => TreeStyle) | null;
  /** Around its cave mouths: lichen colour and boulders (the light spilling in is the sky's). */
  mouth: { glow: RGB; rocks: MouthRockStyle };
  /** Mouths and starts keep this far above the water (m, as its palette sees heights). */
  dry: number;
  /** Average colour of its ground (for the bounce light). */
  albedo: RGB;
  /** For the editor's map (display colours): shore, lowland, highland. */
  map: { shore: RGB; low: RGB; high: RGB };
  /** Where its own snow starts (m, as its palette sees heights); meadows use the world's snow line. */
  snowLine?: number;
  /**
   * When its palette's dry patches are snow (taiga, tundra): how many there are (the palette's
   * dry amount). Grass doesn't grow through them: blades stood up all over the snow.
   */
  snowPatches?: number;
}

/** A new biome from its palette, grass, trees and rocks. */
function biome(
  label: string,
  palette: BiomePalette,
  grass: { lush: RGB; meadow: RGB; tip: RGB; height: number; amount: number },
  species: [SpeciesId, SpeciesId],
  trees: { aShare: [number, number]; lone: number } | null,
  rocks: { light: [RGB, RGB]; dark: [RGB, RGB] } | null,
  more: { dry?: number; albedo: RGB; map: Biome['map']; lichen?: RGB },
): Biome {
  const g: GrassStyle = {
    lush: grass.lush,
    meadow: grass.meadow,
    tip: grass.tip,
    roughness: 0.65,
    height: grass.height,
  };
  return {
    label,
    style: (rig) => biomeStyle(palette, rig.glow ? (p: N) => rig.glowAt(p) : undefined),
    grass: () => g,
    grassAmount: grass.amount,
    snowPatches:
      palette.dry &&
      0.2126 * palette.dry.color[0] + 0.7152 * palette.dry.color[1] + 0.0722 * palette.dry.color[2] > 0.5
        ? palette.dry.amount
        : undefined,
    species,
    trees: trees ? () => ({ aShare: trees.aShare, loneTrees: trees.lone }) : null,
    rocks: rocks ? () => boulders(rocks.light, rocks.dark) : null,
    mouth: {
      glow: more.lichen ?? [0.4, 0.34, 0.2],
      rocks: rocks
        ? { light: rocks.light[1], dark: rocks.dark[0] }
        : { light: palette.rock[1], dark: palette.rock[0] },
    },
    dry: more.dry ?? 3,
    albedo: more.albedo,
    map: more.map,
    snowLine: palette.snow?.line,
  };
}

/** A built-in world's ground, plants and mouths, as a biome. */
function classic(
  label: string,
  def: HollowSceneDef,
  mouth: { glow: RGB; rocks: MouthRockStyle },
  species: [SpeciesId, SpeciesId],
  dry: number,
  map: Biome['map'],
): Biome {
  return {
    label,
    style: def.style,
    grass: def.grass,
    grassAmount: 1,
    species,
    trees: (rig) => {
      const t = def.trees(rig);
      return { aShare: t.aShare, loneTrees: t.loneTrees, minHeight: t.minHeight };
    },
    rocks: def.rocks ?? null,
    mouth,
    dry,
    albedo: def.worldAlbedo,
    map,
  };
}

export const BIOMES: Record<BiomeId, Biome> = {
  meadow: classic('Meadows and forests (Verdant)', VERDANT, VERDANT_MOUTH, ['pine', 'oak'], 4, {
    shore: [0.79, 0.72, 0.54],
    low: [0.36, 0.5, 0.17],
    high: [0.55, 0.49, 0.4],
  }),
  glowmarsh: classic('Glowing marsh (Glowmire)', GLOWMIRE, GLOWMIRE_MOUTH, ['mushroom', 'lantern'], 2, {
    shore: [0.3, 0.52, 0.6],
    low: [0.14, 0.22, 0.28],
    high: [0.3, 0.24, 0.38],
  }),
  lava: classic('Lava fields (Emberdeep)', EMBERDEEP, EMBERDEEP_MOUTH, ['charred', 'ember'], 8, {
    shore: [0.58, 0.5, 0.18],
    low: [0.22, 0.2, 0.19],
    high: [0.4, 0.27, 0.19],
  }),
  darkstone: classic('Dark stone (Umbral)', UMBRAL, UMBRAL_MOUTH, ['crystal', 'bone'], 2, {
    shore: [0.34, 0.33, 0.32],
    low: [0.22, 0.22, 0.23],
    high: [0.38, 0.37, 0.36],
  }),
  desert: biome(
    'Desert: dunes and cacti',
    {
      surface: 'sand',
      ground: [
        [0.335, 0.259, 0.162],
        [0.501, 0.404, 0.265],
      ],
      groundAlt: [
        [0.322, 0.222, 0.133],
        [0.382, 0.29, 0.186],
      ],
      dirt: [0.282, 0.214, 0.136],
      rock: [
        [0.222, 0.154, 0.099],
        [0.327, 0.246, 0.164],
      ],
      sand: [0.55, 0.46, 0.312],
      ripples: 1,
      roughness: 0.95,
    },
    {
      lush: [0.184, 0.172, 0.074],
      meadow: [0.253, 0.225, 0.113],
      tip: [0.388, 0.345, 0.2],
      height: 0.5,
      amount: 0.12,
    },
    ['cactus', 'drybush'],
    { aShare: [0.5, 0.5], lone: 0.012 },
    {
      light: [
        [0.306, 0.223, 0.144],
        [0.384, 0.291, 0.198],
      ],
      dark: [
        [0.24, 0.168, 0.108],
        [0.275, 0.195, 0.126],
      ],
    },
    {
      albedo: [0.258, 0.201, 0.126],
      map: { shore: [0.9, 0.8, 0.58], low: [0.86, 0.72, 0.5], high: [0.72, 0.52, 0.36] },
    },
  ),
  savanna: biome(
    'Savanna: golden grass, acacias',
    {
      ground: [
        [0.24, 0.217, 0.08],
        [0.305, 0.261, 0.109],
      ],
      groundAlt: [
        [0.208, 0.208, 0.081],
        [0.257, 0.235, 0.101],
      ],
      dry: { color: [0.362, 0.304, 0.164], amount: 0.6 },
      forest: {
        canopy: [
          [0.077, 0.123, 0.038],
          [0.108, 0.149, 0.047],
        ],
        cover: 0.08,
        wavelength: 900,
      },
      dirt: [0.265, 0.177, 0.099],
      rock: [
        [0.218, 0.149, 0.1],
        [0.292, 0.213, 0.146],
      ],
      sand: [0.483, 0.401, 0.262],
    },
    {
      lush: [0.233, 0.198, 0.07],
      meadow: [0.324, 0.27, 0.108],
      tip: [0.621, 0.541, 0.286],
      height: 1.4,
      amount: 1,
    },
    ['acacia', 'drybush'],
    { aShare: [0.75, 0.6], lone: 0.02 },
    {
      light: [
        [0.242, 0.196, 0.15],
        [0.286, 0.242, 0.187],
      ],
      dark: [
        [0.189, 0.151, 0.113],
        [0.225, 0.178, 0.13],
      ],
    },
    {
      albedo: [0.232, 0.198, 0.093],
      map: { shore: [0.86, 0.76, 0.52], low: [0.78, 0.68, 0.34], high: [0.62, 0.48, 0.34] },
    },
  ),
  jungle: biome(
    'Jungle: dense, green, flowering',
    {
      ground: [
        [0.058, 0.144, 0.029],
        [0.09, 0.179, 0.038],
      ],
      groundAlt: [
        [0.069, 0.152, 0.055],
        [0.1, 0.187, 0.062],
      ],
      flowers: {
        colors: [
          [0.508, 0.141, 0.169],
          [0.793, 0.585, 0.167],
          [0.389, 0.167, 0.445],
        ],
        amount: 0.5,
      },
      forest: {
        canopy: [
          [0.02, 0.1, 0.02],
          [0.04, 0.127, 0.024],
        ],
        cover: 0.7,
        wavelength: 1500,
      },
      dirt: [0.176, 0.113, 0.056],
      rock: [
        [0.126, 0.126, 0.098],
        [0.192, 0.18, 0.144],
      ],
      sand: [0.526, 0.481, 0.338],
    },
    {
      lush: [0.041, 0.165, 0.027],
      meadow: [0.074, 0.208, 0.037],
      tip: [0.167, 0.277, 0.077],
      height: 1.6,
      amount: 1,
    },
    ['jungletree', 'palm'],
    { aShare: [0.55, 0.8], lone: 0.02 },
    null,
    {
      albedo: [0.06, 0.134, 0.037],
      map: { shore: [0.82, 0.76, 0.54], low: [0.2, 0.44, 0.12], high: [0.3, 0.42, 0.22] },
    },
  ),
  taiga: biome(
    'Taiga: snowy pine forests',
    {
      ground: [
        [0.157, 0.181, 0.157],
        [0.205, 0.217, 0.182],
      ],
      dry: { color: [0.679, 0.713, 0.745], amount: 0.7 },
      forest: {
        canopy: [
          [0.03, 0.08, 0.05],
          [0.044, 0.106, 0.062],
        ],
        cover: 0.55,
        wavelength: 1800,
      },
      dirt: [0.16, 0.133, 0.106],
      rock: [
        [0.16, 0.166, 0.178],
        [0.242, 0.248, 0.259],
      ],
      sand: [0.272, 0.261, 0.229],
      snow: { line: 180 },
    },
    {
      lush: [0.097, 0.139, 0.07],
      meadow: [0.149, 0.173, 0.099],
      tip: [0.224, 0.235, 0.168],
      height: 0.7,
      amount: 0.6,
    },
    ['pine', 'snowpine'],
    { aShare: [0.6, 0.4], lone: 0.01 },
    {
      light: [
        [0.224, 0.229, 0.24],
        [0.271, 0.271, 0.282],
      ],
      dark: [
        [0.16, 0.166, 0.172],
        [0.192, 0.198, 0.203],
      ],
    },
    {
      albedo: [0.177, 0.194, 0.188],
      map: { shore: [0.72, 0.72, 0.68], low: [0.36, 0.44, 0.36], high: [0.86, 0.88, 0.9] },
    },
  ),
  tundra: biome(
    'Tundra: moss, snow patches, low scrub',
    {
      ground: [
        [0.18, 0.18, 0.132],
        [0.228, 0.216, 0.159],
      ],
      groundAlt: [
        [0.167, 0.19, 0.155],
        [0.206, 0.217, 0.172],
      ],
      dry: { color: [0.696, 0.713, 0.746], amount: 0.5 },
      flowers: {
        colors: [
          [0.524, 0.449, 0.598],
          [0.85, 0.85, 0.85],
        ],
        amount: 0.25,
      },
      forest: {
        canopy: [
          [0.048, 0.096, 0.058],
          [0.065, 0.113, 0.065],
        ],
        cover: 0.05,
        wavelength: 800,
      },
      dirt: [0.185, 0.161, 0.124],
      rock: [
        [0.179, 0.179, 0.191],
        [0.271, 0.271, 0.282],
      ],
      sand: [0.319, 0.302, 0.267],
      snow: { line: 90 },
    },
    {
      lush: [0.132, 0.146, 0.079],
      meadow: [0.192, 0.18, 0.108],
      tip: [0.274, 0.252, 0.165],
      height: 0.45,
      amount: 0.7,
    },
    ['shrub', 'smallpine'],
    { aShare: [0.7, 0.8], lone: 0.015 },
    {
      light: [
        [0.243, 0.243, 0.249],
        [0.331, 0.331, 0.337],
      ],
      dark: [
        [0.17, 0.17, 0.176],
        [0.198, 0.198, 0.203],
      ],
    },
    {
      albedo: [0.207, 0.207, 0.172],
      map: { shore: [0.7, 0.68, 0.6], low: [0.5, 0.5, 0.38], high: [0.84, 0.86, 0.88] },
    },
  ),
  snowfield: biome(
    'Snowfields: ice and drifts',
    {
      surface: 'snow',
      // (Snow a little under its real albedo, and matte: at real values it dazzled.)
      ground: [
        [0.63, 0.662, 0.71],
        [0.731, 0.748, 0.781],
      ],
      groundAlt: [
        [0.544, 0.606, 0.7],
        [0.663, 0.713, 0.787],
      ],
      dirt: [0.436, 0.451, 0.48],
      rock: [
        [0.168, 0.18, 0.204],
        [0.302, 0.326, 0.363],
      ],
      sand: [0.585, 0.602, 0.627],
      ripples: 0.5,
      roughness: 0.72,
    },
    {
      lush: [0.283, 0.295, 0.283],
      meadow: [0.433, 0.447, 0.433],
      tip: [0.664, 0.691, 0.709],
      height: 0.3,
      amount: 0,
    },
    ['deadtree', 'snowpine'],
    { aShare: [0.4, 0.3], lone: 0.004 },
    {
      light: [
        [0.222, 0.239, 0.267],
        [0.324, 0.343, 0.374],
      ],
      dark: [
        [0.154, 0.166, 0.185],
        [0.186, 0.197, 0.222],
      ],
    },
    {
      albedo: [0.594, 0.634, 0.681],
      map: { shore: [0.8, 0.84, 0.88], low: [0.9, 0.92, 0.95], high: [0.96, 0.97, 0.99] },
      lichen: [0.3, 0.45, 0.6],
    },
  ),
  badlands: biome(
    'Badlands: red rock and dry scrub',
    {
      surface: 'sand',
      ground: [
        [0.299, 0.155, 0.084],
        [0.348, 0.202, 0.112],
      ],
      groundAlt: [
        [0.33, 0.231, 0.143],
        [0.433, 0.322, 0.198],
      ],
      dry: { color: [0.485, 0.377, 0.242], amount: 0.4 },
      dirt: [0.28, 0.146, 0.079],
      rock: [
        [0.272, 0.116, 0.065],
        [0.392, 0.25, 0.152],
      ],
      sand: [0.416, 0.297, 0.19],
    },
    {
      lush: [0.187, 0.162, 0.075],
      meadow: [0.244, 0.197, 0.092],
      tip: [0.302, 0.247, 0.143],
      height: 0.6,
      amount: 0.3,
    },
    ['deadtree', 'drybush'],
    { aShare: [0.4, 0.5], lone: 0.01 },
    {
      light: [
        [0.316, 0.183, 0.115],
        [0.359, 0.24, 0.153],
      ],
      dark: [
        [0.244, 0.128, 0.077],
        [0.276, 0.156, 0.096],
      ],
    },
    {
      albedo: [0.292, 0.175, 0.105],
      map: { shore: [0.86, 0.66, 0.46], low: [0.8, 0.46, 0.3], high: [0.86, 0.62, 0.42] },
      lichen: [0.5, 0.3, 0.12],
    },
  ),
  ashlands: biome(
    'Ashlands: grey ash, charred woods',
    {
      surface: 'sand',
      ground: [
        [0.089, 0.089, 0.089],
        [0.136, 0.129, 0.123],
      ],
      groundAlt: [
        [0.163, 0.157, 0.15],
        [0.2, 0.193, 0.181],
      ],
      dry: { color: [0.227, 0.215, 0.199], amount: 0.3 },
      forest: {
        canopy: [
          [0.04, 0.04, 0.036],
          [0.07, 0.06, 0.05],
        ],
        cover: 0.15,
        wavelength: 1200,
      },
      dirt: [0.121, 0.106, 0.091],
      rock: [
        [0.06, 0.056, 0.05],
        [0.13, 0.115, 0.101],
      ],
      sand: [0.147, 0.134, 0.12],
      glow: { color: [1.0, 0.35, 0.08], kind: 'veins', strength: 0.6 },
    },
    {
      lush: [0.08, 0.07, 0.06],
      meadow: [0.113, 0.097, 0.081],
      tip: [0.151, 0.124, 0.096],
      height: 0.5,
      amount: 0.25,
    },
    ['charred', 'deadtree'],
    { aShare: [0.7, 0.7], lone: 0.01 },
    {
      light: [
        [0.11, 0.102, 0.094],
        [0.136, 0.129, 0.123],
      ],
      dark: [
        [0.07, 0.066, 0.06],
        [0.093, 0.084, 0.08],
      ],
    },
    {
      dry: 5,
      albedo: [0.119, 0.111, 0.104],
      map: { shore: [0.36, 0.34, 0.32], low: [0.3, 0.29, 0.28], high: [0.44, 0.42, 0.4] },
      lichen: [0.9, 0.3, 0.05],
    },
  ),
  crystal: biome(
    'Crystal fields: glowing spires',
    {
      ground: [
        [0.271, 0.26, 0.314],
        [0.445, 0.431, 0.503],
      ],
      groundAlt: [
        [0.245, 0.29, 0.334],
        [0.415, 0.473, 0.532],
      ],
      flowers: {
        colors: [
          [0.53, 0.706, 0.884],
          [0.73, 0.486, 0.811],
        ],
        amount: 0.3,
      },
      forest: {
        canopy: [
          [0.173, 0.202, 0.316],
          [0.221, 0.233, 0.344],
        ],
        cover: 0.2,
        wavelength: 700,
      },
      dirt: [0.226, 0.215, 0.248],
      rock: [
        [0.181, 0.169, 0.229],
        [0.334, 0.315, 0.401],
      ],
      sand: [0.565, 0.548, 0.597],
      glow: { color: [0.4, 0.7, 1.0], kind: 'specks', strength: 1.2 },
      roughness: 0.6,
    },
    {
      lush: [0.175, 0.198, 0.245],
      meadow: [0.221, 0.243, 0.288],
      tip: [0.484, 0.565, 0.687],
      height: 0.5,
      amount: 0.2,
    },
    ['crystal', 'bone'],
    { aShare: [0.8, 0.8], lone: 0.02 },
    {
      light: [
        [0.253, 0.242, 0.296],
        [0.353, 0.341, 0.404],
      ],
      dark: [
        [0.18, 0.174, 0.216],
        [0.216, 0.206, 0.251],
      ],
    },
    {
      albedo: [0.277, 0.277, 0.333],
      map: { shore: [0.8, 0.78, 0.86], low: [0.7, 0.68, 0.82], high: [0.62, 0.6, 0.76] },
      lichen: [0.2, 0.5, 1.0],
    },
  ),
  alien: biome(
    'Alien: violet and teal, glowing',
    {
      ground: [
        [0.194, 0.074, 0.238],
        [0.234, 0.104, 0.273],
      ],
      groundAlt: [
        [0.051, 0.178, 0.191],
        [0.07, 0.223, 0.223],
      ],
      flowers: {
        colors: [
          [0.681, 0.341, 0.137],
          [0.278, 0.927, 0.649],
          [0.943, 0.849, 0.283],
        ],
        amount: 0.5,
      },
      forest: {
        canopy: [
          [0.178, 0.045, 0.267],
          [0.065, 0.162, 0.195],
        ],
        cover: 0.35,
        wavelength: 1100,
      },
      dirt: [0.159, 0.08, 0.144],
      rock: [
        [0.109, 0.073, 0.164],
        [0.192, 0.128, 0.231],
      ],
      sand: [0.254, 0.204, 0.283],
      glow: { color: [0.3, 1.0, 0.8], kind: 'patches', strength: 1.0 },
    },
    {
      lush: [0.186, 0.051, 0.254],
      meadow: [0.062, 0.186, 0.199],
      tip: [0.301, 0.12, 0.361],
      height: 1.2,
      amount: 0.9,
    },
    ['mushroom', 'lantern'],
    { aShare: [0.6, 0.35], lone: 0.015 },
    {
      light: [
        [0.165, 0.11, 0.206],
        [0.2, 0.138, 0.238],
      ],
      dark: [
        [0.102, 0.085, 0.153],
        [0.132, 0.103, 0.176],
      ],
    },
    {
      albedo: [0.144, 0.101, 0.202],
      map: { shore: [0.62, 0.5, 0.7], low: [0.5, 0.26, 0.6], high: [0.26, 0.56, 0.58] },
      lichen: [0.3, 1.0, 0.8],
    },
  ),
  autumn: biome(
    'Autumn woods: red and gold',
    {
      ground: [
        [0.187, 0.162, 0.05],
        [0.236, 0.189, 0.059],
      ],
      groundAlt: [
        [0.233, 0.13, 0.039],
        [0.267, 0.158, 0.048],
      ],
      dry: { color: [0.284, 0.205, 0.079], amount: 0.3 },
      forest: {
        canopy: [
          [0.235, 0.088, 0.022],
          [0.264, 0.138, 0.025],
        ],
        cover: 0.45,
        wavelength: 1600,
      },
      dirt: [0.189, 0.122, 0.068],
      rock: [
        [0.16, 0.133, 0.106],
        [0.241, 0.206, 0.16],
      ],
      sand: [0.45, 0.395, 0.287],
    },
    {
      lush: [0.182, 0.182, 0.049],
      meadow: [0.259, 0.207, 0.058],
      tip: [0.329, 0.247, 0.076],
      height: 0.9,
      amount: 0.9,
    },
    ['autumnoak', 'goldbirch'],
    { aShare: [0.6, 0.6], lone: 0.012 },
    {
      light: [
        [0.209, 0.197, 0.18],
        [0.273, 0.256, 0.229],
      ],
      dark: [
        [0.165, 0.152, 0.121],
        [0.196, 0.184, 0.154],
      ],
    },
    {
      albedo: [0.205, 0.141, 0.051],
      map: { shore: [0.8, 0.72, 0.54], low: [0.74, 0.5, 0.2], high: [0.6, 0.48, 0.36] },
    },
  ),
  heath: biome(
    'Heath: purple moorland',
    {
      ground: [
        [0.179, 0.11, 0.165],
        [0.213, 0.138, 0.188],
      ],
      groundAlt: [
        [0.151, 0.164, 0.076],
        [0.19, 0.19, 0.095],
      ],
      flowers: {
        colors: [
          [0.339, 0.17, 0.339],
          [0.618, 0.464, 0.618],
        ],
        amount: 0.6,
      },
      forest: {
        canopy: [
          [0.048, 0.096, 0.058],
          [0.065, 0.113, 0.065],
        ],
        cover: 0.08,
        wavelength: 900,
      },
      dirt: [0.172, 0.132, 0.106],
      rock: [
        [0.163, 0.157, 0.15],
        [0.245, 0.234, 0.222],
      ],
      sand: [0.38, 0.348, 0.285],
      snow: { line: 700 },
    },
    {
      lush: [0.147, 0.103, 0.132],
      meadow: [0.189, 0.151, 0.101],
      tip: [0.261, 0.174, 0.244],
      height: 0.55,
      amount: 0.8,
    },
    ['pine', 'shrub'],
    { aShare: [0.5, 0.7], lone: 0.008 },
    {
      light: [
        [0.226, 0.22, 0.208],
        [0.271, 0.261, 0.25],
      ],
      dark: [
        [0.163, 0.157, 0.15],
        [0.194, 0.189, 0.183],
      ],
    },
    {
      albedo: [0.171, 0.131, 0.145],
      map: { shore: [0.76, 0.7, 0.6], low: [0.56, 0.38, 0.54], high: [0.5, 0.48, 0.34] },
    },
  ),
};

// --- Presets --------------------------------------------------------------------------------------

export interface Preset {
  label: string;
  type: PlanetTypeId;
  sky: SkyId;
  biomes: Omit<BiomeSlot, 'treeA' | 'treeB'>[];
}

export const PRESETS: Record<PresetId, Preset> = {
  verdant: {
    label: 'Verdant: sunlit meadows',
    type: 'continents',
    sky: 'sunlit',
    biomes: [{ id: 'meadow', share: 1, where: 'anywhere' }],
  },
  earthlike: {
    label: 'Earthlike: forests, deserts, ice',
    type: 'continents',
    sky: 'sunlit',
    biomes: [
      { id: 'meadow', share: 0.45, where: 'anywhere' },
      { id: 'desert', share: 0.2, where: 'equator' },
      { id: 'taiga', share: 0.2, where: 'poles' },
      { id: 'snowfield', share: 0.15, where: 'high' },
    ],
  },
  glowmire: {
    label: 'Glowmire: a glowing marsh',
    type: 'marsh',
    sky: 'glowing',
    biomes: [{ id: 'glowmarsh', share: 1, where: 'anywhere' }],
  },
  emberdeep: {
    label: 'Emberdeep: lava and ash',
    type: 'volcanic',
    sky: 'ember',
    biomes: [{ id: 'lava', share: 1, where: 'anywhere' }],
  },
  umbral: {
    label: 'Umbral: dark stone',
    type: 'cavern',
    sky: 'dark',
    biomes: [{ id: 'darkstone', share: 1, where: 'anywhere' }],
  },
  desert: {
    label: 'Desert world',
    type: 'plains',
    sky: 'golden',
    biomes: [
      { id: 'desert', share: 0.75, where: 'anywhere' },
      { id: 'badlands', share: 0.25, where: 'high' },
    ],
  },
  savanna: {
    label: 'Savanna world',
    type: 'plains',
    sky: 'golden',
    biomes: [
      { id: 'savanna', share: 0.7, where: 'anywhere' },
      { id: 'desert', share: 0.3, where: 'equator' },
    ],
  },
  ice: {
    label: 'Ice world',
    type: 'highlands',
    sky: 'pale',
    biomes: [
      { id: 'snowfield', share: 0.6, where: 'anywhere' },
      { id: 'tundra', share: 0.4, where: 'low' },
    ],
  },
  jungle: {
    label: 'Jungle islands',
    type: 'archipelago',
    sky: 'sunlit',
    biomes: [
      { id: 'jungle', share: 0.8, where: 'low' },
      { id: 'meadow', share: 0.2, where: 'high' },
    ],
  },
  autumn: {
    label: 'Autumn world',
    type: 'continents',
    sky: 'sunlit',
    biomes: [
      { id: 'autumn', share: 0.6, where: 'anywhere' },
      { id: 'heath', share: 0.4, where: 'high' },
    ],
  },
  alien: {
    label: 'Alien world',
    type: 'shattered',
    sky: 'alien',
    biomes: [
      { id: 'alien', share: 0.6, where: 'anywhere' },
      { id: 'crystal', share: 0.4, where: 'high' },
    ],
  },
  ash: {
    label: 'Ash world',
    type: 'volcanic',
    sky: 'ember',
    biomes: [
      { id: 'ashlands', share: 0.6, where: 'high' },
      { id: 'lava', share: 0.4, where: 'low' },
    ],
  },
  crystal: {
    label: 'Crystal caverns',
    type: 'cavern',
    sky: 'dark',
    biomes: [
      { id: 'crystal', share: 0.7, where: 'anywhere' },
      { id: 'darkstone', share: 0.3, where: 'low' },
    ],
  },
  ocean: {
    label: 'Ocean world',
    type: 'ocean',
    sky: 'sunlit',
    biomes: [
      { id: 'jungle', share: 0.5, where: 'low' },
      { id: 'heath', share: 0.5, where: 'high' },
    ],
  },
};
